/**
 * Auto-reframe with active-speaker tracking — OpusClip's "ReframeAnything"
 * for /auto-captions. When the user picks a non-native aspect (9:16, 1:1,
 * 4:5) the speaker stays centred horizontally as they move through the
 * original wider frame, instead of the burn quietly exporting at native
 * resolution and cropping the speaker off-screen.
 *
 * Why this matters: aspect-ratio buttons in /auto-captions have been
 * purely visual (CSS letterbox over the preview) and never actually
 * cropped the burned MP4. So if a speaker is on the right third of a
 * 16:9 webcam frame and the user picked 9:16, the burned vertical video
 * showed mostly empty room and the speaker cropped off-screen. This
 * module re-uses the per-second face track that auto-captions-face.js
 * already builds for caption placement, smooths it with an EMA over the
 * centre point, and emits an FFmpeg crop filter that pans the crop
 * window horizontally to follow the speaker.
 *
 * Public API (window.__acAutoReframe):
 *   isEnabled()                — true when stage has data-aspect != ''
 *                                AND face samples exist.
 *   getAspectRatio()           — '9:16' | '1:1' | '4:5' | '16:9' | '' .
 *   smoothFaceTrack(samples, opts) — EMA smoothing (default alpha=0.15)
 *                                over the dominant-face centre. Returns
 *                                per-sample [{t, cx, cy}] in [0..1].
 *   buildCropFilter(track, srcW, srcH, targetW, targetH, opts)
 *                              — Returns FFmpeg filter string. Uses
 *                                sendcmd to inject per-keyframe crop
 *                                x updates (every ~0.5 s after smoothing).
 *   planForBurn(state)         — Convenience: read stage aspect, smooth
 *                                state.faceSamples, return either
 *                                { filter, targetW, targetH } or null
 *                                if not applicable (no aspect / no faces
 *                                / aspect wider-or-equal than source).
 *
 * The crop output is forced to even dimensions because libx264 + yuv420p
 * requires even width and height — odd values produce hard FFmpeg
 * failures. The crop window is clamped to [0, srcW - targetW] so we
 * never run off the edges of the source frame.
 *
 * Tolerant to both the {x, y, w, h} shape that auto-captions-face.js
 * actually emits, and the {x, y, width, height} shape that some callers
 * (and the spec) document.
 */
(function(){
    'use strict';

    var ASPECT_MAP = {
        '9:16': { num: 9,  den: 16 },
        '1:1':  { num: 1,  den: 1  },
        '4:5':  { num: 4,  den: 5  },
        '16:9': { num: 16, den: 9  },
    };

    /* Resolve face dimension keys — face.js uses w/h, the spec docs use
       width/height. Tolerate both so the caller doesn't have to. */
    function faceW(f) {
        if (f == null) return 0;
        return (typeof f.w === 'number') ? f.w
             : (typeof f.width === 'number') ? f.width
             : 0;
    }
    function faceH(f) {
        if (f == null) return 0;
        return (typeof f.h === 'number') ? f.h
             : (typeof f.height === 'number') ? f.height
             : 0;
    }

    /* Pick the dominant face per sample — largest area, which mirrors
       what auto-captions-face.placementForLine already does. Returns
       {cx, cy} normalised to [0..1] or null if the sample has no face. */
    function dominantFaceCenter(sample) {
        if (!sample || !sample.faces || !sample.faces.length) return null;
        var best = null, bestArea = -1;
        for (var i = 0; i < sample.faces.length; i++) {
            var f = sample.faces[i];
            var w = faceW(f), h = faceH(f);
            var area = w * h;
            if (area > bestArea) { bestArea = area; best = f; }
        }
        if (!best || bestArea <= 0) return null;
        return {
            cx: (best.x || 0) + faceW(best) * 0.5,
            cy: (best.y || 0) + faceH(best) * 0.5,
        };
    }

    /* EMA smoothing over the dominant-face centre. We must smooth HARD
       because a face bbox that jitters between 700px and 720px would
       otherwise produce visible crop jitter (the user reads the video
       as "wobbling"). Default alpha=0.15 was tuned for ~1-Hz face
       sampling on standard talking-head content; lower (0.10) for
       jittery webcam input.

       Samples with no face are interpolated by holding the previous
       smoothed value — better than emitting a "no-op centre" frame
       that would yank the crop back to the middle of the source. */
    function smoothFaceTrack(samples, opts) {
        opts = opts || {};
        var alpha = (typeof opts.alpha === 'number') ? opts.alpha : 0.15;
        if (alpha < 0.02) alpha = 0.02;
        if (alpha > 0.6)  alpha = 0.6;
        if (!Array.isArray(samples) || !samples.length) return [];

        // Initialise smoothed state from the FIRST sample that actually
        // has a face. If no sample has a face, return an empty track.
        var initIdx = -1, init = null;
        for (var k = 0; k < samples.length; k++) {
            var c0 = dominantFaceCenter(samples[k]);
            if (c0) { init = c0; initIdx = k; break; }
        }
        if (initIdx < 0) return [];

        var smoothed = [];
        var smCx = init.cx, smCy = init.cy;
        for (var i = 0; i < samples.length; i++) {
            var s = samples[i];
            var c = dominantFaceCenter(s);
            if (c) {
                // Classic EMA: new = alpha*observed + (1-alpha)*prev
                smCx = alpha * c.cx + (1 - alpha) * smCx;
                smCy = alpha * c.cy + (1 - alpha) * smCy;
            }
            // If no face, hold previous smCx/smCy (sticky tracking).
            smoothed.push({
                t: (typeof s.t === 'number') ? s.t : i,
                cx: smCx,
                cy: smCy,
            });
        }
        return smoothed;
    }

    /* Force value to nearest even integer in [lo, hi]. libx264 yuv420p
       refuses odd output dimensions. */
    function evenClamp(v, lo, hi) {
        v = Math.round(v);
        if (v % 2 !== 0) v -= 1;
        if (v < lo) v = lo;
        if (v > hi) v = hi;
        // ensure final is even within bounds
        if (v % 2 !== 0) v += 1;
        if (v > hi) v -= 2;
        return v;
    }

    /* Compute the FFmpeg crop X position for a smoothed centre, clamped
       to source bounds. Cx is in [0..1] of source width. */
    function cropXFromCenter(cx, srcW, targetW) {
        var px = Math.round(cx * srcW - targetW / 2);
        if (px < 0) px = 0;
        var maxX = srcW - targetW;
        if (px > maxX) px = maxX;
        // Even-pixel snap for libx264 (some hardware encoders refuse odd
        // crop offsets even when output dimensions are even).
        if (px % 2 !== 0) px -= 1;
        if (px < 0) px = 0;
        return px;
    }

    /* Build a sendcmd-driven crop filter. sendcmd is FFmpeg's mechanism
       for re-parameterising a filter mid-stream — every command fires
       at a specified PTS and replaces the named filter argument. We
       drop a `crop x N` command at each keyframe in the smoothed track
       (deduped to ~2 Hz so the output isn't bloated for long videos).

       The filter graph is:
         sendcmd=c='T1 crop x V1; T2 crop x V2; ...',crop=W:H:0:Y
       The initial X=0 is overwritten the moment the first command fires
       (at T=0). Y is constant — we hold the vertical crop at the centre
       of the source because vertical talking-head movement is rare and
       sendcmd jitter on two axes amplifies wobble.

       Returns a filter string suitable for passing as `extraFilters` to
       VideoExporter.plan() (chain-comma syntax, no labelled streams).
    */
    function buildCropFilter(track, srcW, srcH, targetW, targetH, opts) {
        opts = opts || {};
        var keyframeMin = (typeof opts.keyframeMinSec === 'number') ? opts.keyframeMinSec : 0.5;

        // Defensive: if no track, just emit a centre-crop — at least
        // the burn will respect the chosen aspect ratio instead of
        // exporting at native resolution.
        var Y = evenClamp((srcH - targetH) / 2, 0, srcH - targetH);
        if (!Array.isArray(track) || !track.length) {
            var Xc = evenClamp((srcW - targetW) / 2, 0, srcW - targetW);
            return 'crop=' + targetW + ':' + targetH + ':' + Xc + ':' + Y;
        }

        // Build sendcmd command list. Skip commands that don't change X
        // by more than 1 even-pixel since the previous emitted command —
        // saves filter-string size on long videos with a still speaker.
        var cmds = [];
        var lastX = null;
        var lastEmitT = -Infinity;
        for (var i = 0; i < track.length; i++) {
            var sm = track[i];
            var t = (typeof sm.t === 'number' && sm.t >= 0) ? sm.t : 0;
            // Dedup by minimum time gap.
            if (t - lastEmitT < keyframeMin && i !== 0) continue;
            var X = cropXFromCenter(sm.cx, srcW, targetW);
            if (lastX !== null && Math.abs(X - lastX) < 2 && i !== 0) continue;
            // sendcmd PTS-based syntax: "T <delta> [filterTarget] cmd args"
            // We don't use intervals (T1-T2), just instantaneous commands.
            cmds.push(t.toFixed(3) + ' crop x ' + X);
            lastX = X;
            lastEmitT = t;
        }

        // If no commands were emitted (shouldn't happen since the first
        // sample is always emitted), fall back to centre crop.
        if (!cmds.length) {
            var Xc2 = evenClamp((srcW - targetW) / 2, 0, srcW - targetW);
            return 'crop=' + targetW + ':' + targetH + ':' + Xc2 + ':' + Y;
        }

        // Initial X = first command's value so frame 0 isn't visually
        // wrong before sendcmd fires.
        var firstX = (function(){
            var first = cmds[0];
            var m = first.match(/crop x (\d+)/);
            return m ? parseInt(m[1], 10) : 0;
        })();

        // sendcmd 'c=' value: commands joined by ';'.
        // FFmpeg filter syntax requires escaping ',' inside the arg with
        // a backslash if commas are present — our commands don't contain
        // commas so the plain single-quoted form is safe.
        var sendcmd = "sendcmd=c='" + cmds.join('; ') + "'";
        var crop = 'crop=' + targetW + ':' + targetH + ':' + firstX + ':' + Y;
        return sendcmd + ',' + crop;
    }

    /* Read the active aspect ratio from the stage data-attribute. Empty
       string = native (no crop, no auto-reframe needed). */
    function getAspectRatio() {
        var stage = document.querySelector('.ac-stage');
        if (!stage) return '';
        return stage.getAttribute('data-aspect') || '';
    }

    /* True when stage has a non-native aspect set AND faceSamples were
       collected by auto-captions-face.js for the current video. */
    function isEnabled() {
        var ar = getAspectRatio();
        if (!ar || ar === '16:9') return false; // 16:9 native for most uploads — no crop wanted
        // Be tolerant about where state lives — pipeline owns it under
        // a closure, but exposes via the burn path. We re-read from a
        // best-effort hook the pipeline can install onto window.
        var samples = (window.__acState && window.__acState.faceSamples) ? window.__acState.faceSamples : null;
        if (!samples || !samples.length) return false;
        var withFace = 0;
        for (var i = 0; i < samples.length; i++) {
            if (samples[i] && samples[i].faces && samples[i].faces.length) withFace++;
        }
        return withFace > 0;
    }

    /* Convenience plan-for-burn. Pass in the pipeline's `state` object
       (anything with .faceSamples, .videoWidth, .videoHeight) and get a
       ready-to-plumb extraFilters string. Returns null if not
       applicable so the caller can fall back to the standard burn.

       state shape:
         faceSamples: [{t, faces: [{x, y, w, h}, ...]}, ...]   normalised
         videoWidth, videoHeight: source pixels (preferred)
         videoEl: HTMLVideoElement fallback for dimensions
    */
    function planForBurn(state) {
        if (!state) return null;
        var ar = getAspectRatio();
        if (!ar) return null;

        var spec = ASPECT_MAP[ar];
        if (!spec) return null;

        var srcW = state.videoWidth || (state.videoEl && state.videoEl.videoWidth) || 0;
        var srcH = state.videoHeight || (state.videoEl && state.videoEl.videoHeight) || 0;
        if (!srcW || !srcH) return null;

        // Target width: srcH * num / den. Then clamp to srcW (if requested
        // aspect is WIDER than source, no horizontal crop possible and
        // auto-reframe degenerates to identity — bail).
        var rawTargetW = srcH * spec.num / spec.den;
        var rawTargetH = srcH;
        // If requested aspect is wider than source, crop height instead.
        if (rawTargetW > srcW) {
            rawTargetW = srcW;
            rawTargetH = srcW * spec.den / spec.num;
        }
        var targetW = evenClamp(rawTargetW, 16, srcW);
        var targetH = evenClamp(rawTargetH, 16, srcH);
        if (targetW >= srcW && targetH >= srcH) return null; // identity — no need

        var samples = state.faceSamples;
        if (!Array.isArray(samples) || !samples.length) {
            // No face track — emit a centre crop so the burn at least
            // respects the chosen aspect. Caller (UI) should have warned
            // the user already via the "no face" banner.
            var centerFilter = buildCropFilter([], srcW, srcH, targetW, targetH);
            return {
                filter:  centerFilter,
                targetW: targetW,
                targetH: targetH,
                tracked: false,
            };
        }

        var track = smoothFaceTrack(samples, {});
        if (!track.length) {
            var centerFilter2 = buildCropFilter([], srcW, srcH, targetW, targetH);
            return { filter: centerFilter2, targetW: targetW, targetH: targetH, tracked: false };
        }
        var filter = buildCropFilter(track, srcW, srcH, targetW, targetH, { keyframeMinSec: 0.5 });
        return { filter: filter, targetW: targetW, targetH: targetH, tracked: true };
    }

    window.__acAutoReframe = {
        isEnabled: isEnabled,
        getAspectRatio: getAspectRatio,
        smoothFaceTrack: smoothFaceTrack,
        buildCropFilter: buildCropFilter,
        planForBurn: planForBurn,
        // Exposed for tests + the UI toggle (so the burn path can read a
        // single source of truth):
        _aspectMap: ASPECT_MAP,
        _evenClamp: evenClamp,
        _cropXFromCenter: cropXFromCenter,
    };
})();
